import { createSlice } from '@reduxjs/toolkit';
import { getProfileType } from './helpers';
import { createProfileThunks } from './thunks';
export const profileSliceInitialState = {
    isLoggedIn: false,
    username: null,
    profileAuth: {
        loading: false,
        error: null,
        account: null,
        numberOfRetries: 0,
        accountMerged: false,
    },
    profileSummary: {
        private: { loading: true, error: null, data: null },
        public: { loading: true, error: null, data: null },
    },
    profileDetails: { loading: true, error: null, data: null },
    profileLeaderboard: { loading: false, error: null, data: null },
    publicLeaderboard: { loading: false, error: null, data: null, totalPages: null },
    topPublicLeaderboard: { loading: false, error: null, data: null },
    updateUsername: { loading: false, error: null },
    logOut: {
        loading: false,
        error: null,
    },
};
export const createProfileState = () => {
    const thunks = createProfileThunks();
    const slice = createSlice({
        name: 'profile',
        initialState: profileSliceInitialState,
        reducers: {
            logInUser: (state) => {
                state.isLoggedIn = true;
            },
            logOutUser: () => {
                return profileSliceInitialState;
            },
            clearAuthError: (state) => {
                state.profileAuth.error = null;
            },
            resetMergeStatus: (state) => {
                state.profileAuth.accountMerged = false;
            },
            resetUserData: (state) => {
                state.profileSummary.public = profileSliceInitialState.profileSummary.public;
            },
            resetUpdateUsernameError: (state) => {
                state.updateUsername.error = null;
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(thunks.setClaim.fulfilled, (state, action) => {
                state.profileSummary.private.data = action.payload;
            })
                .addCase(thunks.signMessage.pending, (state, action) => {
                state.profileAuth.loading = true;
                state.profileAuth.error = null;
                state.profileAuth.account = action.meta.arg.account;
                state.profileAuth.numberOfRetries = 0;
            })
                .addCase(thunks.signMessage.rejected, (state, action) => {
                state.profileAuth.loading = false;
                state.profileAuth.account = null;
                state.profileAuth.numberOfRetries = 0;
                state.profileAuth.error = action.payload ?? null;
            })
                .addCase(thunks.signMessage.fulfilled, (state, action) => {
                state.profileAuth.loading = false;
                state.profileSummary.private.loading = false;
                state.profileAuth.error = null;
                state.profileAuth.account = null;
                if (action.payload) {
                    if (state.isLoggedIn) {
                        state.profileAuth.accountMerged = true;
                    }
                    else {
                        state.isLoggedIn = true;
                    }
                    state.profileSummary.private.data = action.payload;
                    if (action.payload?.username) {
                        state.username = action.payload?.username;
                    }
                    state.profileAuth.numberOfRetries = 0;
                }
                else {
                    state.profileAuth.numberOfRetries += 1;
                }
            })
                .addCase(thunks.fetchProfileSummary.pending, (state, action) => {
                const { meta: { arg }, } = action;
                const key = getProfileType(arg.public);
                state.profileSummary[key].data = null;
                state.profileSummary[key].loading = true;
                state.profileSummary[key].error = null;
            })
                .addCase(thunks.fetchProfileSummary.fulfilled, (state, action) => {
                const key = getProfileType(action.meta.arg.public);
                state.profileSummary[key].loading = false;
                state.profileSummary[key].error = null;
                state.profileSummary[key].data = action.payload;
                if (action.payload?.username && key === 'private') {
                    state.username = action.payload?.username;
                }
            })
                .addCase(thunks.fetchProfileSummary.rejected, (state, action) => {
                /**
                 * In DEV mode react strict will call the action twice.
                 * We added a check here to make sure if the first request has been cancelled,
                 * we don't update the state in between of these two requests.
                 */
                if (!action.payload || action.error.name === 'AbortError') {
                    return;
                }
                const key = getProfileType(action.meta.arg.public);
                state.profileSummary[key].loading = false;
                state.profileSummary[key].error =
                    typeof action.payload === 'string' ? action.payload : null;
                state.profileSummary[key].data = null;
            })
                .addCase(thunks.fetchProfileDetails.pending, (state) => {
                state.profileDetails.data = null;
                state.profileDetails.loading = true;
                state.profileDetails.error = null;
            })
                .addCase(thunks.fetchProfileDetails.fulfilled, (state, action) => {
                state.profileDetails.loading = false;
                state.profileDetails.error = null;
                state.profileDetails.data = action.payload;
            })
                .addCase(thunks.fetchProfileDetails.rejected, (state, action) => {
                /**
                 * In DEV mode react strict will call the action twice.
                 * We added a check here to make sure if the first request has been cancelled,
                 * we don't update the state in between of these two requests.
                 */
                if (action.error.name === 'AbortError') {
                    return;
                }
                state.profileDetails.loading = false;
                state.profileDetails.error = action.payload ?? null;
                state.profileDetails.data = null;
            })
                .addCase(thunks.fetchProfileLeaderboard.pending, (state) => {
                state.profileLeaderboard.data = null;
                state.profileLeaderboard.loading = true;
                state.profileLeaderboard.error = null;
            })
                .addCase(thunks.fetchProfileLeaderboard.fulfilled, (state, action) => {
                state.profileLeaderboard.loading = false;
                state.profileLeaderboard.error = null;
                state.profileLeaderboard.data = action.payload;
            })
                .addCase(thunks.fetchProfileLeaderboard.rejected, (state, action) => {
                if (!action.payload) {
                    return;
                }
                state.profileLeaderboard.loading = false;
                state.profileLeaderboard.error = action.payload ?? null;
            })
                .addCase(thunks.fetchPublicLeaderboard.pending, (state, action) => {
                state.publicLeaderboard.loading = true;
                state.publicLeaderboard.error = null;
                if (action.meta.arg.page === 0) {
                    state.topPublicLeaderboard.loading = true;
                    state.topPublicLeaderboard.error = null;
                }
            })
                .addCase(thunks.fetchPublicLeaderboard.fulfilled, (state, action) => {
                state.publicLeaderboard.loading = false;
                state.publicLeaderboard.error = null;
                state.publicLeaderboard.data = action.payload.leaderboard;
                state.publicLeaderboard.totalPages = action.payload.totalPages;
                if (action.meta.arg.page === 0) {
                    state.topPublicLeaderboard.loading = false;
                    state.topPublicLeaderboard.error = null;
                    state.topPublicLeaderboard.data = action.payload.leaderboard;
                }
            })
                .addCase(thunks.fetchPublicLeaderboard.rejected, (state, action) => {
                state.publicLeaderboard.loading = false;
                state.publicLeaderboard.error = action.payload ?? null;
                if (action.meta.arg.page === 0) {
                    state.topPublicLeaderboard.loading = false;
                    state.topPublicLeaderboard.error = action.payload ?? null;
                }
            })
                .addCase(thunks.fetchTopPublicLeaderboard.pending, (state) => {
                state.topPublicLeaderboard.loading = true;
                state.topPublicLeaderboard.error = null;
            })
                .addCase(thunks.fetchTopPublicLeaderboard.fulfilled, (state, action) => {
                state.topPublicLeaderboard.loading = false;
                state.topPublicLeaderboard.error = null;
                state.topPublicLeaderboard.data = action.payload;
            })
                .addCase(thunks.fetchTopPublicLeaderboard.rejected, (state, action) => {
                state.topPublicLeaderboard.loading = false;
                state.topPublicLeaderboard.error = action.payload ?? null;
            })
                .addCase(thunks.updateUsername.pending, (state) => {
                state.updateUsername.loading = true;
                state.updateUsername.error = null;
            })
                .addCase(thunks.updateUsername.fulfilled, (state, action) => {
                state.updateUsername.loading = false;
                state.username = action.payload.username;
            })
                .addCase(thunks.updateUsername.rejected, (state, action) => {
                state.updateUsername.loading = false;
                state.updateUsername.error = action.payload ?? null;
            })
                .addCase(thunks.requestLogOutUser.pending, (state) => {
                state.logOut.loading = true;
                state.logOut.error = null;
            })
                .addCase(thunks.requestLogOutUser.fulfilled, () => profileSliceInitialState)
                .addCase(thunks.requestLogOutUser.rejected, (state, action) => {
                state.logOut.loading = false;
                state.logOut.error = action.payload ?? null;
            });
        },
    });
    return {
        slice,
        thunks,
    };
};
